import { Modal, notification, Show } from '@pankod/refine-antd';
import { file2Base64, useNavigation } from '@pankod/refine-core';
import { ProductForm } from 'components/product';
import { ICategory, IMeasurementUnit, ITag } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllCategoryDropdown } from 'services/category';
import { getMeasurementUnitsDropdown } from 'services/measurement-units';
import { createProductWithVariants, ProductWithVariantsData } from 'services/product';
import { getAllTagDropdown } from 'services/tag';
import { getTemplates, ITemplate } from 'services/template';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { optionalPurchasePriceFlag } from 'utilities/product-purchase-price';

export const ProductCreate = () => {
  const [categoryOptions, setCategoryOptions] = useState<ICategory[]>([]);
  const [tagOptions, setTagOptions] = useState<ITag[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<IWarehouseDropDown[]>([]);
  const [templateOptions, setTemplateOptions] = useState<ITemplate[]>([]);
  const [measurementUnitOptions, setMeasurementUnitOptions] = useState<
    Omit<IMeasurementUnit, 'description'>[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { push } = useNavigation();
  const optionalPurchasePriceEnabled = useFeatureFlag(optionalPurchasePriceFlag);

  useEffect(() => {
    async function fetchOptions() {
      try {
        setIsLoading(true);
        const [
          categoryResponse,
          tagResponse,
          warehouseResponse,
          templateResponse,
          measurementUnitsResponse,
        ] = await Promise.all([
          getAllCategoryDropdown(),
          getAllTagDropdown(),
          getWarehousesDropdown(),
          getTemplates(),
          getMeasurementUnitsDropdown(),
        ]);

        setCategoryOptions(categoryResponse);
        setTagOptions(tagResponse);
        setWarehouseOptions(warehouseResponse);
        setTemplateOptions(templateResponse);
        setMeasurementUnitOptions(measurementUnitsResponse);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchOptions();
  }, []);

  // Guard browser refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleFormSubmit = async (values: any) => {
    const { hasBatch: _, hasVariant: __, variantDetails, ...restFormValues } = values;
    const payload: ProductWithVariantsData = {
      ...restFormValues,
      variantsDetails: variantDetails.map((variant: any) => {
        const { attributeFields, customFields, templateFields } = variant;

        // make attribute payload
        const attributeKeys = Object.keys(attributeFields ?? {});
        const modifiedAttributeFields = attributeKeys.map((key) => ({
          key,
          value: attributeFields[key],
        }));

        // make custom field payload
        const customFiledKeys = Object.keys(customFields ?? {});
        const modifiedCustomFields = customFiledKeys
          .map((key) => {
            const customFieldValue = customFields[key];
            if (customFieldValue !== undefined) {
              return {
                key,
                value: customFieldValue.toString(),
              };
            } else {
              return undefined;
            }
          })
          .filter(Boolean);

        // make template field payload
        const templateFieldKeys = Object.keys(templateFields ?? {});
        const modifiedTemplateFields = templateFieldKeys.map((key) => ({
          templateId: key,
          templateAttributeData: templateFields[key],
        }));

        return {
          ...variant,
          // Stitches S2: the purchase price column is hidden when adding, so send 0.
          ...(optionalPurchasePriceEnabled && { purchasePrice: variant.purchasePrice ?? 0 }),
          sellingPrice: variant.sellingPrice ?? 0,
          attributeFields: modifiedAttributeFields,
          customFields: modifiedCustomFields,
          templateFields: modifiedTemplateFields,
        };
      }),
    };

    // modify image payload to base64string
    const { variantsDetails } = payload;
    for (const variant of variantsDetails) {
      const imageFile =
        variant.photo && variant.photo.length && variant.photo[0]?.status !== 'error'
          ? variant.photo[0]
          : null;
      let base64File: string | null = null;
      if (imageFile) {
        if (imageFile.originFileObj) {
          const base64String = await file2Base64(imageFile);
          const { originFileObj, response, status, xhr, ...rest } = imageFile;
          base64File = { ...rest, base64String };
        } else {
          base64File = imageFile;
        }
      }
      variant.photo = base64File;
    }

    const makeProduct = restFormValues.make ?? false;

    setIsCreating(true);
    try {
      const { id: productId } = await createProductWithVariants(payload);
      notification.success({
        message: 'Success',
        description: 'Successfully created.',
      });
      const url = makeProduct ? `/products/edit/${productId}?activeTab=bom-create` : '/products';
      push(url);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsCreating(false);
  };

  return (
    <Show
      title="Create Product"
      headerProps={{
        extra: null,
        onBack: () => {
          if (!isDirty) {
            push('/products');
            return;
          }
          Modal.confirm({
            title: 'Unsaved Changes',
            content: 'You have unsaved changes. Are you sure you want to leave?',
            okText: 'Leave',
            cancelText: 'Stay',
            onOk: () => push('/products'),
          });
        },
      }}
    >
      <ProductForm
        type="add"
        isFetching={isLoading}
        isSubmitting={isCreating}
        categoryOptions={categoryOptions}
        tagOptions={tagOptions}
        warehouseOptions={warehouseOptions}
        templateOptions={templateOptions}
        measurementUnitOptions={measurementUnitOptions}
        onDirtyChange={setIsDirty}
        onSubmit={handleFormSubmit}
      />
    </Show>
  );
};
